/**
 * Facility Data Model
 * Comprehensive facility information with NDIS, Aged Care, and Business Group support
 */

export const createFacility = (data = {}) => {
  return {
    id: data.id || null,
    
    // Basic Information
    name: data.name || '',
    legalName: data.legalName || null,
    abn: data.abn || null, // Australian Business Number
    
    // Address
    address: {
      street: data.address?.street || '',
      city: data.address?.city || '',
      state: data.address?.state || '',
      postcode: data.address?.postcode || '',
      country: data.address?.country || 'Australia',
      fullAddress: data.address?.fullAddress || null // Formatted full address
    },
    
    // Contact Information
    phone: data.phone || null,
    email: data.email || null,
    website: data.website || null,
    fax: data.fax || null,
    
    // Facility Types
    facilityTypes: data.facilityTypes || [], // Array: ['ndis', 'agedcare', 'hospital']
    primaryType: data.primaryType || null, // 'ndis', 'agedcare', 'hospital', 'dual'
    
    // NDIS Configuration
    ndis: {
      enabled: data.ndis?.enabled ?? false,
      ndisProviderNumber: data.ndis?.ndisProviderNumber || null,
      ndisType: data.ndis?.ndisType || null, // 'home', 'sda', 'silo', 'group_home', 'community', 'support_services'
      ndisRegistrationDate: data.ndis?.ndisRegistrationDate || null,
      ndisExpiryDate: data.ndis?.ndisExpiryDate || null,
      supportsCategories: data.ndis?.supportsCategories || [], // ['core', 'capacity_building', 'capital']
      serviceAreas: data.ndis?.serviceAreas || [], // Geographic areas served
      prodaDeviceId: data.ndis?.prodaDeviceId || null
    },
    
    // Aged Care Configuration
    agedCare: {
      enabled: data.agedCare?.enabled ?? false,
      providerNumber: data.agedCare?.providerNumber || null,
      agedCareType: data.agedCare?.agedCareType || null, // 'residential', 'home_care', 'respite', 'transitional'
      accreditationNumber: data.agedCare?.accreditationNumber || null,
      accreditationExpiry: data.agedCare?.accreditationExpiry || null,
      beds: data.agedCare?.beds || 0,
      occupancy: data.agedCare?.occupancy || 0,
      careLevels: data.agedCare?.careLevels || [], // ['low', 'medium', 'high', 'dementia', 'palliative']
      anaccEnabled: data.agedCare?.anaccEnabled ?? false,
      enrmcEnabled: data.agedCare?.enrmcEnabled ?? false,
      supportAtHomeEnabled: data.agedCare?.supportAtHomeEnabled ?? false
    },
    
    // Business Group
    businessGroup: {
      id: data.businessGroup?.id || null,
      name: data.businessGroup?.name || null,
      abn: data.businessGroup?.abn || null,
      corporateStructure: data.businessGroup?.corporateStructure || null, // 'single', 'group', 'franchise'
      parentCompany: data.businessGroup?.parentCompany || null
    },
    
    // Operational Information
    manager: data.manager || null,
    managerId: data.managerId || null,
    totalWards: data.totalWards || 0,
    totalResidents: data.totalResidents || 0,
    totalParticipants: data.totalParticipants || 0, // For NDIS
    totalStaff: data.totalStaff || 0,
    
    // Ratings & Compliance
    rating: data.rating || null, // Star rating
    accreditationStatus: data.accreditationStatus || 'active', // 'active', 'suspended', 'expired'
    lastInspectionDate: data.lastInspectionDate || null,
    nextInspectionDate: data.nextInspectionDate || null,
    
    // Financial
    fundingSource: data.fundingSource || [], // ['ndis', 'aged_care', 'private', 'dva']
    billingContact: data.billingContact || null,
    
    // System Configuration
    systemContext: data.systemContext || 'agedcare', // 'ndis', 'agedcare', 'hospital'
    features: data.features || {
      anacc: false,
      enrmc: false,
      supportAtHome: false,
      restrictivePractices: false,
      ndisBudget: false,
      shadowLedger: false,
      nomineeManagement: false,
      participantIdentity: false
    },
    
    // Status
    status: data.status || 'active', // 'active', 'inactive', 'suspended', 'closed'
    
    // Metadata
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
    createdBy: data.createdBy || null
  };
};

export const NDIS_TYPES = {
  HOME: 'home', // Home-based support
  SDA: 'sda', // Specialist Disability Accommodation
  SILO: 'silo', // Supported Independent Living Option
  GROUP_HOME: 'group_home', // Group home
  COMMUNITY: 'community', // Community-based services
  SUPPORT_SERVICES: 'support_services' // Support services provider
};

export const AGED_CARE_TYPES = {
  RESIDENTIAL: 'residential', // Residential aged care
  HOME_CARE: 'home_care', // Home care packages
  RESPITE: 'respite', // Respite care
  TRANSITIONAL: 'transitional', // Transitional care
  DUAL: 'dual' // Both residential and home care
};

export const BUSINESS_GROUP_STRUCTURES = {
  SINGLE: 'single', // Single facility business
  GROUP: 'group', // Business group with multiple facilities
  FRANCHISE: 'franchise' // Franchise structure
};

/**
 * Determine facility features based on types
 */
export const calculateFacilityFeatures = (facility) => {
  const features = {
    anacc: false,
    enrmc: false,
    supportAtHome: false,
    restrictivePractices: false,
    ndisBudget: false,
    shadowLedger: false,
    nomineeManagement: false,
    participantIdentity: false
  };
  
  // Aged Care features
  if (facility.agedCare?.enabled) {
    features.anacc = facility.agedCare.anaccEnabled || facility.agedCare.agedCareType === 'residential';
    features.enrmc = facility.agedCare.enrmcEnabled || facility.agedCare.agedCareType === 'residential';
    features.supportAtHome = facility.agedCare.supportAtHomeEnabled || facility.agedCare.agedCareType === 'home_care';
  }
  
  // NDIS features
  if (facility.ndis?.enabled) {
    features.restrictivePractices = true;
    features.ndisBudget = true;
    features.shadowLedger = true;
    features.nomineeManagement = true;
    features.participantIdentity = true;
  }
  
  return features;
};

export default {
  createFacility,
  NDIS_TYPES,
  AGED_CARE_TYPES,
  BUSINESS_GROUP_STRUCTURES,
  calculateFacilityFeatures
};

